// Custom deck.gl Layer: renders one adaptive-mesh-tile-chunker.py tile
// (positions + triangle indices + per-vertex scalar) directly, with
// Gouraud interpolation and a diverging colormap computed in the
// fragment shader -- no rasterization, no GeoTIFF.
//
// Geometry (positions + indices) is built once via luma.gl's Geometry
// helper, which is static per tile. The scalar/wetMask buffers are kept
// separate and created/managed by hand so that a timestep change can
// just call Buffer.write() on the existing GPU buffers instead of
// rebuilding the whole model -- positions/indices for a given tile never
// change, only which timestep's scalar values are bound.
import {Layer, project32} from '@deck.gl/core';
import {Model, Geometry} from '@luma.gl/engine';

const glslUniformBlock = `\
layout(std140) uniform meshTileUniforms {
  float colorMin;
  float colorMax;
  float colormapId;
} meshTile;
`;

const meshTileUniforms = {
  name: 'meshTile',
  vs: glslUniformBlock,
  fs: glslUniformBlock,
  uniformTypes: {
    colorMin: 'f32',
    colorMax: 'f32',
    colormapId: 'f32'
  }
};

const vs = `#version 300 es
#define SHADER_NAME mesh-tile-layer-vertex-shader

in vec3 positions;
in float scalar;
in float wetMask;

out float vScalar;
out float vWetMask;

void main(void) {
  vScalar = scalar;
  vWetMask = wetMask;
  geometry.worldPosition = positions;
  gl_Position = project_position_to_clipspace(positions, vec3(0.0), vec3(0.0), geometry.position);
  // Slight clip-Z bias toward the camera so fill wins over the coplanar
  // basemap when zoomed in (depth fighting punched holes through the contour).
  // Keep this smaller than the wireframe nudge so edges still draw on top.
  // Shader nudge avoids D3D11 issues seen with rasterizer depthBias.
  gl_Position.z -= 1.5e-4 * gl_Position.w;
  DECKGL_FILTER_GL_POSITION(gl_Position, geometry);
}
`;

const fs = `#version 300 es
#define SHADER_NAME mesh-tile-layer-fragment-shader
precision highp float;

in float vScalar;
in float vWetMask;

out vec4 fragColor;

// Colormap id matches COLORMAPS[].glslId in colormaps.js / the colorbar CSS.

vec3 jetColor(float t) {
  float r = clamp(1.5 - abs(4.0 * t - 3.0), 0.0, 1.0);
  float g = clamp(1.5 - abs(4.0 * t - 2.0), 0.0, 1.0);
  float b = clamp(1.5 - abs(4.0 * t - 1.0), 0.0, 1.0);
  return vec3(r, g, b);
}

vec3 bluesColor(float t) {
  // Approximate matplotlib Blues (light → dark).
  vec3 c0 = vec3(0.969, 0.984, 1.0);
  vec3 c1 = vec3(0.419, 0.682, 0.839);
  vec3 c2 = vec3(0.031, 0.188, 0.420);
  if (t < 0.5) return mix(c0, c1, t * 2.0);
  return mix(c1, c2, (t - 0.5) * 2.0);
}

vec3 redsColor(float t) {
  // Approximate matplotlib Reds (light → dark).
  vec3 c0 = vec3(1.0, 0.961, 0.941);
  vec3 c1 = vec3(0.984, 0.416, 0.290);
  vec3 c2 = vec3(0.404, 0.0, 0.051);
  if (t < 0.5) return mix(c0, c1, t * 2.0);
  return mix(c1, c2, (t - 0.5) * 2.0);
}

// Polynomial approximations (Inigo Quilez / matplotlib-style).
vec3 viridisColor(float t) {
  const vec3 c0 = vec3(0.2777273273, 0.0054073445, 0.3340998053);
  const vec3 c1 = vec3(0.1050930431, 1.4046135295, 1.3845901626);
  const vec3 c2 = vec3(-0.3308618288, 0.2148475494, 0.0950957959);
  const vec3 c3 = vec3(-4.6342304989, -5.7991009731, -19.3324409563);
  const vec3 c4 = vec3(6.2282699364, 14.1799333660, 56.6905526000);
  const vec3 c5 = vec3(4.7703338738, -13.7451453770, -65.3530326330);
  const vec3 c6 = vec3(-5.4354558559, 4.6458526121, 26.3124352496);
  return clamp(c0 + t * (c1 + t * (c2 + t * (c3 + t * (c4 + t * (c5 + t * c6))))), 0.0, 1.0);
}

vec3 plasmaColor(float t) {
  const vec3 c0 = vec3(0.0587323439, 0.0233367080, 0.5433401820);
  const vec3 c1 = vec3(2.1765146342, 0.2383834177, 0.7539604590);
  const vec3 c2 = vec3(-2.6894605685, -7.4559527530, 3.1107885490);
  const vec3 c3 = vec3(6.1303483456, 42.3460768210, -28.5188546530);
  const vec3 c4 = vec3(-11.1074361905, -82.6663117659, 60.1398474910);
  const vec3 c5 = vec3(10.0230655763, 71.4136177015, -54.0721865550);
  const vec3 c6 = vec3(-3.6587138424, -22.9315349706, 18.1919078430);
  return clamp(c0 + t * (c1 + t * (c2 + t * (c3 + t * (c4 + t * (c5 + t * c6))))), 0.0, 1.0);
}

vec3 turboColor(float t) {
  // Google Turbo (approx).
  const vec3 c0 = vec3(0.13572138, 0.06160259, 0.9799226);
  const vec3 c1 = vec3(4.61539260, 5.89713559, 4.68592545);
  const vec3 c2 = vec3(-42.66032258, -52.60777934, -16.56661538);
  const vec3 c3 = vec3(132.18514981, 137.36234687, 3.97285426);
  const vec3 c4 = vec3(-152.94239396, -131.48478608, 18.43755997);
  const vec3 c5 = vec3(59.28637943, 43.38676552, -12.08554835);
  return clamp(c0 + t * (c1 + t * (c2 + t * (c3 + t * (c4 + t * c5)))), 0.0, 1.0);
}

vec3 coolwarmColor(float t) {
  vec3 cold = vec3(0.230, 0.299, 0.754);
  vec3 mid = vec3(0.865, 0.865, 0.865);
  vec3 warm = vec3(0.706, 0.016, 0.150);
  if (t < 0.5) return mix(cold, mid, t * 2.0);
  return mix(mid, warm, (t - 0.5) * 2.0);
}

vec3 infernoColor(float t) {
  const vec3 c0 = vec3(0.0002189404, 0.0016510048, -0.0194808983);
  const vec3 c1 = vec3(0.1065133393, 0.5639564368, 3.9327123889);
  const vec3 c2 = vec3(11.6024930823, -3.9728539657, -15.9423941063);
  const vec3 c3 = vec3(-41.7039961314, 17.4363988820, 44.3307918753);
  const vec3 c4 = vec3(77.1629356994, -33.4023959988, -81.8073092573);
  const vec3 c5 = vec3(-71.3195161545, 32.6260642639, 73.2095198580);
  const vec3 c6 = vec3(25.1311262245, -12.2426689523, -23.0703258517);
  return clamp(c0 + t * (c1 + t * (c2 + t * (c3 + t * (c4 + t * (c5 + t * c6))))), 0.0, 1.0);
}

vec3 cividisColor(float t) {
  // Piecewise approx of matplotlib cividis.
  vec3 c0 = vec3(0.0, 0.135, 0.304);
  vec3 c1 = vec3(0.357, 0.486, 0.545);
  vec3 c2 = vec3(0.976, 0.878, 0.282);
  if (t < 0.5) return mix(c0, c1, t * 2.0);
  return mix(c1, c2, (t - 0.5) * 2.0);
}

// Topo–bathy: land brown → white at 0.5 → deep-ocean blue.
// Intended for ADCIRC depth (neg = land, pos = below geoid) with a
// domain centered on 0 so white maps to sea level.
vec3 topoBathyColor(float t) {
  vec3 landDeep = vec3(0.361, 0.200, 0.090); // #5c3317
  vec3 landMid = vec3(0.545, 0.353, 0.169);  // #8b5a2b
  vec3 landLight = vec3(0.769, 0.647, 0.455); // #c4a574
  vec3 sand = vec3(0.910, 0.863, 0.784);      // #e8dcc8
  vec3 white = vec3(1.0, 1.0, 1.0);
  vec3 shallow = vec3(0.784, 0.878, 0.941);   // #c8e0f0
  vec3 midBlue = vec3(0.420, 0.682, 0.839);   // #6baed6
  vec3 deepBlue = vec3(0.129, 0.443, 0.710);  // #2171b5
  vec3 abyss = vec3(0.031, 0.188, 0.420);     // #08306b
  if (t < 0.15) return mix(landDeep, landMid, t / 0.15);
  if (t < 0.30) return mix(landMid, landLight, (t - 0.15) / 0.15);
  if (t < 0.42) return mix(landLight, sand, (t - 0.30) / 0.12);
  if (t < 0.50) return mix(sand, white, (t - 0.42) / 0.08);
  if (t < 0.58) return mix(white, shallow, (t - 0.50) / 0.08);
  if (t < 0.70) return mix(shallow, midBlue, (t - 0.58) / 0.12);
  if (t < 0.85) return mix(midBlue, deepBlue, (t - 0.70) / 0.15);
  return mix(deepBlue, abyss, (t - 0.85) / 0.15);
}

vec3 applyColormap(float t, float id) {
  int cid = int(id + 0.5);
  if (cid == 0) return jetColor(t);
  if (cid == 1) return bluesColor(t);
  if (cid == 2) return bluesColor(1.0 - t);
  if (cid == 3) return redsColor(t);
  if (cid == 4) return redsColor(1.0 - t);
  if (cid == 5) return viridisColor(t);
  if (cid == 6) return plasmaColor(t);
  if (cid == 7) return turboColor(t);
  if (cid == 8) return coolwarmColor(t);
  if (cid == 9) return infernoColor(t);
  if (cid == 10) return cividisColor(t);
  if (cid == 11) return vec3(t);
  if (cid == 12) return vec3(1.0 - t);
  if (cid == 13) return topoBathyColor(t);
  return jetColor(t);
}

void main(void) {
  // Vertex-interpolated wet/dry mask: any fragment touched by a dry
  // (fill-value) vertex is discarded. Since it's Gouraud-interpolated,
  // this erodes triangles smoothly away from dry vertices instead of
  // showing a hard edge or a blown-up interpolated fill value.
  if (vWetMask < 0.999) {
    discard;
  }
  float t = clamp((vScalar - meshTile.colorMin) / (meshTile.colorMax - meshTile.colorMin), 0.0, 1.0);
  // layer.opacity is deck.gl's standard per-layer opacity uniform (set via
  // the layer's opacity prop), always available regardless of which
  // shader modules are included.
  fragColor = vec4(applyColormap(t, meshTile.colormapId), layer.opacity);
  DECKGL_FILTER_COLOR(fragColor, geometry);
}
`;

// luma.gl passes a Model's `parameters` through as-is on every draw call
// (it does NOT merge missing fields with safe defaults -- see the
// "WebGL shares underlying cached pipelines..." comment in
// @luma.gl/engine's model.js). A partial object that only sets blend
// fields can leave depth/cull state as whatever the previous draw call
// left it at, which silently broke the wireframe pass: it rendered fine
// wherever the fill pass had discarded (dry) fragments, but vanished
// wherever fill had already written a fragment first, because leftover
// depth state from the fill draw made the second (wireframe) draw fail
// the depth test on equal depth values. Spelling out every field here
// avoids relying on that state carrying over correctly between draws.
const BLEND_PARAMETERS = {
  blend: true,
  blendColorSrcFactor: 'src-alpha',
  blendColorDstFactor: 'one-minus-src-alpha',
  blendAlphaSrcFactor: 'one',
  blendAlphaDstFactor: 'one-minus-src-alpha',
  // GlobeView defaults to cullMode:'back', which deck.gl merges into every
  // layer's draw parameters. Split antimeridian triangles often flip winding
  // relative to the source mesh, so backface culling drops fill at the seam
  // while wireframe (lines, depthCompare:'always') still shows. Depth test
  // alone hides the far hemisphere; do not rely on backface culling here.
  cullMode: 'none',
  depthWriteEnabled: true,
  depthCompare: 'less-equal'
};

// Wireframe draws after fill at the same surface depth. depthCompare:'always'
// made far-side antimeridian edges visible through the globe when Show Mesh
// Edges is on. Re-enable the depth test (no depth writes) and nudge clip Z
// slightly toward the camera so lines still win over coincident fill nearby.
const EDGE_PARAMETERS = {
  blend: true,
  blendColorSrcFactor: 'src-alpha',
  blendColorDstFactor: 'one-minus-src-alpha',
  blendAlphaSrcFactor: 'one',
  blendAlphaDstFactor: 'one-minus-src-alpha',
  cullMode: 'back',
  depthWriteEnabled: false,
  depthCompare: 'less-equal'
};

// Mesh wireframe overlay -- a separate line-list draw over the same
// shared position buffer (no vertex duplication needed). Built lazily,
// only the first time showEdges turns on for a given tile, so leaving it
// off (the default) costs nothing at all.
const edgeVs = `#version 300 es
#define SHADER_NAME mesh-tile-layer-edge-vertex-shader

in vec3 positions;

void main(void) {
  gl_Position = project_position_to_clipspace(positions, vec3(0.0), vec3(0.0), geometry.position);
  // Bias toward camera so lines pass depth test over fill drawn at the same
  // surface (shader nudge avoids D3D11 rasterizer-state issues from depthBias).
  gl_Position.z -= 3e-4 * gl_Position.w;
  DECKGL_FILTER_GL_POSITION(gl_Position, geometry);
}
`;

const edgeFs = `#version 300 es
#define SHADER_NAME mesh-tile-layer-edge-fragment-shader
precision highp float;

out vec4 fragColor;

void main(void) {
  fragColor = vec4(0.0, 0.0, 0.0, 0.35 * layer.opacity);
  DECKGL_FILTER_COLOR(fragColor, geometry);
}
`;

// Each triangle (a, b, c) contributes 3 edges -- 6 indices into the same
// positions buffer the fill pass already uses. Cheap single pass over an
// array we already have in memory.
function buildEdgeIndices(triangleIndices) {
  const nTris = triangleIndices.length / 3;
  const edgeIndices = new Uint32Array(nTris * 6);
  for (let i = 0; i < nTris; i++) {
    const a = triangleIndices[i * 3];
    const b = triangleIndices[i * 3 + 1];
    const c = triangleIndices[i * 3 + 2];
    const o = i * 6;
    edgeIndices[o] = a;
    edgeIndices[o + 1] = b;
    edgeIndices[o + 2] = b;
    edgeIndices[o + 3] = c;
    edgeIndices[o + 4] = c;
    edgeIndices[o + 5] = a;
  }
  return edgeIndices;
}

class MeshTileLayer extends Layer {
  getShaders() {
    return super.getShaders({vs, fs, modules: [project32, meshTileUniforms]});
  }

  initializeState() {
    // No accessor-driven attributes -- geometry and the scalar/wetMask
    // buffers are built directly from pre-fetched typed arrays, see
    // module comment above.
    this.getAttributeManager().remove(['instancePickingColors']);
  }

  // Return only the fill model. deck.gl's applyModelParameters() runs on
  // getModels() before draw() and would overwrite wireframeModel's
  // EDGE_PARAMETERS with the layer's depth-test settings every frame.
  // Wireframe is drawn manually in draw() with its own parameters and
  // shader props.
  getModels() {
    const {model} = this.state;
    return model ? [model] : [];
  }

  updateState({props, oldProps, changeFlags}) {
    super.updateState({props, oldProps, changeFlags});

    const geometryChanged =
      !this.state.model ||
      changeFlags.extensionsChanged ||
      props.positions3 !== oldProps.positions3 ||
      props.triangleIndices !== oldProps.triangleIndices;

    if (geometryChanged) {
      this._destroyScalarBuffers();
      this.state.model?.destroy();
      this.state.wireframeModel?.destroy();
      const model = this._getModel();
      const wireframeModel = props.showEdges ? this._getWireframeModel() : null;
      this.setState({model, wireframeModel});
      this._createScalarBuffers(model, props.scalarValues, props.wetMaskValues);
    } else if (props.scalarValues !== oldProps.scalarValues || props.wetMaskValues !== oldProps.wetMaskValues) {
      if (this.state.scalarBuffer && this.state.wetMaskBuffer) {
        this.state.scalarBuffer.write(props.scalarValues);
        this.state.wetMaskBuffer.write(props.wetMaskValues);
      } else if (this.state.model) {
        this._createScalarBuffers(this.state.model, props.scalarValues, props.wetMaskValues);
      }
    } else if (props.showEdges && !this.state.wireframeModel) {
      this.setState({wireframeModel: this._getWireframeModel()});
    }
  }

  finalizeState() {
    this._destroyScalarBuffers();
    this.state.wireframeModel?.destroy();
  }

  _destroyScalarBuffers() {
    this.state.scalarBuffer?.destroy();
    this.state.wetMaskBuffer?.destroy();
  }

  _createScalarBuffers(model, scalarValues, wetMaskValues) {
    const {device} = this.context;
    const scalarBuffer = device.createBuffer({data: scalarValues, id: `${this.props.id}-scalar`});
    const wetMaskBuffer = device.createBuffer({data: wetMaskValues, id: `${this.props.id}-wetmask`});
    model.setAttributes({scalar: scalarBuffer, wetMask: wetMaskBuffer});
    this.setState({scalarBuffer, wetMaskBuffer});
  }

  _getModel() {
    const {positions3, triangleIndices} = this.props;

    const geometry = new Geometry({
      topology: 'triangle-list',
      indices: {value: triangleIndices},
      attributes: {
        positions: {size: 3, value: positions3}
      }
    });

    return new Model(this.context.device, {
      ...this.getShaders(),
      id: this.props.id,
      geometry,
      // scalar/wetMask aren't part of the Geometry above (they're
      // uploaded separately in _createScalarBuffers), so their buffer
      // layout has to be declared explicitly here -- Model merges this
      // with whatever Geometry derives for positions.
      bufferLayout: [
        {name: 'scalar', format: 'float32'},
        {name: 'wetMask', format: 'float32'}
      ],
      isInstanced: false,
      // luma.gl disables blending by default; without this, the
      // opacity/transparency slider (layer.opacity in the fragment
      // shader) would have no visible effect.
      parameters: BLEND_PARAMETERS
    });
  }

  _getWireframeModel() {
    const {positions3, triangleIndices} = this.props;
    const edgeIndices = buildEdgeIndices(triangleIndices);

    const geometry = new Geometry({
      topology: 'line-list',
      indices: {value: edgeIndices},
      attributes: {
        positions: {size: 3, value: positions3}
      }
    });

    return new Model(this.context.device, {
      // super.getShaders (not this.getShaders) -- this layer's own
      // getShaders() override always returns the jet-colormap fill
      // shaders, which isn't what the edge pass needs.
      ...super.getShaders({vs: edgeVs, fs: edgeFs, modules: [project32]}),
      id: `${this.props.id}-edges`,
      geometry,
      isInstanced: false,
      parameters: EDGE_PARAMETERS
    });
  }

  draw({renderPass, shaderModuleProps}) {
    const {model, wireframeModel} = this.state;
    if (!model) return;
    const {colorDomain, showEdges, colormapId} = this.props;
    model.shaderInputs.setProps({
      meshTile: {
        colorMin: colorDomain[0],
        colorMax: colorDomain[1],
        colormapId: colormapId
      }
    });
    model.draw(renderPass);

    if (showEdges && wireframeModel) {
      if (shaderModuleProps) {
        wireframeModel.shaderInputs.setProps(shaderModuleProps);
      }
      wireframeModel.draw(renderPass);
    }
  }
}

MeshTileLayer.layerName = 'MeshTileLayer';
MeshTileLayer.defaultProps = {
  positions3: {type: 'array', value: null},
  triangleIndices: {type: 'array', value: null},
  scalarValues: {type: 'array', value: null},
  wetMaskValues: {type: 'array', value: null},
  colorDomain: {type: 'array', value: [-1.5, 1.5], compare: true},
  colormapId: {type: 'number', value: 0},
  showEdges: {type: 'boolean', value: false},
  // Full BLEND_PARAMETERS object -- deck.gl's applyModelParameters() replaces
  // each model's parameters with this every frame; a partial object (depth
  // only) was stripping blend state and forcing per-tile pipeline rebuilds
  // when draw() tried to restore it, which tanked performance with many tiles.
  parameters: BLEND_PARAMETERS
};

export default MeshTileLayer;
